// Plain expression evaluator for graph figures. It matches assessmentTokenizeExpression and
// assessmentEvaluateExpression in api/ai/_lib/assessment_figures.php, so a graph that passed the
// server's check draws the same curve here. It never uses eval.

const TOKEN = /\s*(\d+(?:\.\d+)?|sqrt|abs|sin|cos|tan|pi|x|[-+*/^()])\s*/y;
const FUNCTIONS = ['sqrt', 'abs', 'sin', 'cos', 'tan'];

export function tokenizeExpression(expression) {
    const source = String(expression ?? '').toLowerCase().trim()
        .replace(/^\s*(y|f\s*\(\s*x\s*\)|g\s*\(\s*x\s*\)|h\s*\(\s*x\s*\))\s*=/, '')
        .replace(/[×·]/g, '*').replace(/−/g, '-').replace(/÷/g, '/');
    const raw = [];
    TOKEN.lastIndex = 0;
    while (TOKEN.lastIndex < source.length) {
        const match = TOKEN.exec(source);
        if (!match) throw new Error('Unsupported characters in expression.');
        raw.push(match[1]);
    }

    // Insert the multiplication a teacher would write implicitly: 2x, 3(x+1), x(x-2), )(.
    const tokens = [];
    for (const token of raw) {
        const previous = tokens[tokens.length - 1];
        const endsValue = previous !== undefined && (/^\d/.test(previous) || ['x', 'pi', ')'].includes(previous));
        const startsValue = /^\d/.test(token) || ['x', 'pi', '('].includes(token) || FUNCTIONS.includes(token);
        if (endsValue && startsValue) tokens.push('*');
        tokens.push(token);
    }
    return tokens;
}

export function evaluateExpression(tokens, x) {
    let position = 0;
    const peek = () => tokens[position];
    const next = () => tokens[position++];
    const radians = (degrees) => degrees * Math.PI / 180;

    function primary() {
        const token = next();
        if (token === undefined) throw new Error('Incomplete expression.');
        if (/^\d/.test(token)) return Number(token);
        if (token === 'x') return x;
        if (token === 'pi') return Math.PI;
        if (token === '(') {
            const value = expression();
            if (next() !== ')') throw new Error('Missing closing bracket.');
            return value;
        }
        if (FUNCTIONS.includes(token)) {
            if (next() !== '(') throw new Error('Functions need brackets.');
            const argument = expression();
            if (next() !== ')') throw new Error('Missing closing bracket.');
            switch (token) {
                case 'sqrt': return argument < 0 ? NaN : Math.sqrt(argument);
                case 'abs': return Math.abs(argument);
                case 'sin': return Math.sin(radians(argument));
                case 'cos': return Math.cos(radians(argument));
                default: return Math.tan(radians(argument));
            }
        }
        throw new Error(`Unexpected ${token}.`);
    }
    function power() {
        const base = primary();
        if (peek() === '^') {
            next();
            return base ** unary();
        }
        return base;
    }
    function unary() {
        if (peek() === '-') { next(); return -unary(); }
        if (peek() === '+') { next(); return unary(); }
        return power();
    }
    function term() {
        let value = unary();
        while (peek() === '*' || peek() === '/') {
            const operator = next();
            const right = unary();
            value = operator === '*' ? value * right : (right === 0 ? NaN : value / right);
        }
        return value;
    }
    function expression() {
        let value = term();
        while (peek() === '+' || peek() === '-') {
            value = next() === '+' ? value + term() : value - term();
        }
        return value;
    }

    const result = expression();
    if (position !== tokens.length) throw new Error(`Unexpected ${tokens[position]}.`);
    return result;
}

export function compileExpression(expression) {
    const tokens = tokenizeExpression(expression);
    return (x) => evaluateExpression(tokens, x);
}
